import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { env } from '../config/env';
import { oauth2Config } from '../config/oauth2.config';
import { SocialLoginButtons } from '../components/SocialLoginButtons';

export const LoginPage: React.FC = () => {
  const { loginWithCredentials, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleCredentialLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await loginWithCredentials(username, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.response?.data?.message || 'Invalid username or password. Please try again.';
      setError(message);
      console.error('Login error:', err);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[480px] space-y-10">
        <div className="text-center space-y-4">
          <Link to="/" className="flex flex-col items-center gap-4">
            {env.LOGO_URL ? (
              <img src={env.LOGO_URL} alt={env.COMPANY_NAME} className="h-12 w-auto object-contain" />
            ) : (
              <div className="size-12 bg-primary rounded-xl flex items-center justify-center text-white">
                <span className="material-symbols-outlined text-3xl">shopping_bag</span>
              </div>
            )}
            <h1 className="text-3xl font-black tracking-tight uppercase text-[#111418] dark:text-white">
              {env.COMPANY_NAME}
            </h1>
          </Link>
          <p className="text-sm font-medium text-gray-500 max-w-xs mx-auto">Please sign in to your account</p>
        </div>

        <div className="bg-white dark:bg-black p-10 md:p-12 rounded-[2.5rem] border border-border/40 shadow-2xl shadow-primary/5 space-y-8">
          {/* Username/Password Login Form */}
          <form onSubmit={handleCredentialLogin} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-semibold">Username or Email</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username or email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-12 rounded-xl border border-gray-200 dark:border-gray-700 focus-visible:ring-2 focus-visible:ring-primary/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 rounded-xl border border-gray-200 dark:border-gray-700 pr-12 focus-visible:ring-2 focus-visible:ring-primary/50"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-red-500 text-sm font-medium">{error}</div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                size="lg"
                className="w-full h-14 rounded-2xl text-base font-black shadow-xl shadow-primary/10 transition-all hover:-translate-y-0.5 bg-[#111418] dark:bg-white text-white dark:text-black flex items-center justify-center gap-3 border-0"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </div>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-100 dark:border-gray-800"></span>
            </div>
            <div className="relative flex justify-center text-[10px] font-black uppercase tracking-widest leading-none">
              <span className="bg-white dark:bg-black px-4 text-gray-300">Or continue with</span>
            </div>
          </div>

          <SocialLoginButtons variant="signin" returnTo={from} />
        </div>

        <div className="flex flex-col items-center gap-4">
          <Link to={`/forgot-password?client_id=${oauth2Config.clientId}`} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-primary transition-colors">
            Forgot password?
          </Link>
          <Link to={`/register?client_id=${oauth2Config.clientId}`} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-primary transition-colors flex items-center gap-2">
            Don't have an account? Register <ArrowRight className="size-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};
